import { pages } from './common';
import { PageName } from './pages-helpers';
import { hasReadPermission } from './rbacHelpers';
const normalizePath = (path: string) => {
  if (!path) return '/';
  const trimmed = path.replace(/\/$/, '');
  return trimmed === '' ? '/' : trimmed;
};

/** Order must match useProfileAvailableRouteIds / profile.helpers */
const profileLandingSegments: { submodule: string; segment: string }[] = [
  { submodule: 'personalDetails', segment: 'personal-details' },
  { submodule: 'documents', segment: PageName.documents },
  { submodule: 'calendar', segment: PageName.calendar },
  { submodule: 'leaves', segment: PageName.leaves },
];

function getGuardianProfilePath(): string {
  const profileSeg =
    pages.guardian.subPages?.[PageName.profile]?.path?.replace(/^\//, '') ?? 'profile';
  return `${pages.guardian.path}/${profileSeg}`;
}

function getGuardianDashboardPath(): string {
  const d =
    pages.guardian.subPages?.[PageName.dashboard]?.path?.replace(/^\//, '') ?? 'dashboard';
  return `${pages.guardian.path}/${d}`;
}

/**
 * First main-app profile tab path the user can read, or null if none.
 */
export function getMainAppProfileLandingPath(
  rbac: Record<string, unknown> | null
): string | null {
  if (!rbac) return null;
  for (const { submodule, segment } of profileLandingSegments) {
    if (hasReadPermission(rbac, 'profile', submodule)) {
      return `${pages.profile.path}/${segment}`;
    }
  }
  return null;
}

export type SafeLandingPathOptions = {
  isGuardian: boolean;
  /** When set, avoids redirecting to the same path (uses fallbacks instead). */
  currentPathname?: string;
};

/**
 * When `primary` would equal `cur`, return `secondary` instead, and vice versa
 * to avoid a redirect loop.
 */
function firstDifferentPath(
  cur: string,
  primary: string,
  secondary: string
): string {
  const p = normalizePath(primary);
  const s = normalizePath(secondary);
  if (!cur) return primary;
  if (cur === p) return secondary;
  if (cur === s) return primary;
  return primary;
}

/**
 * Default safe route after RBAC denial: profile (first allowed tab) for staff app,
 * guardian profile for guardian app, with dashboard / guardian home fallbacks.
 */
export function getSafeLandingPath(
  rbac: Record<string, unknown> | null,
  options: SafeLandingPathOptions
): string {
  const { isGuardian, currentPathname } = options;
  const cur = currentPathname ? normalizePath(currentPathname) : '';

  /** Guardian app: landing is auth-based only (no staff/guardian RBAC). */
  if (isGuardian) {
    return firstDifferentPath(
      cur,
      getGuardianProfilePath(),
      getGuardianDashboardPath()
    );
  }

  const profilePath = getMainAppProfileLandingPath(rbac);
  if (profilePath) {
    if (cur && normalizePath(profilePath) === cur) {
      return firstDifferentPath(cur, pages.dashboard.path, pages.profile.path);
    }
    return profilePath;
  }

  return firstDifferentPath(cur, pages.dashboard.path, pages.profile.path);
}
